// ============================================================
// usePlayerPresence - Player heartbeat, staleness, and kick detection
// ============================================================
// Extracted from useFirestoreSync. Handles:
//   - Heartbeat interval (15s)
//   - Player listener with kick detection (and re-seating, v8.310.4;
//     through a dropped line, 2026-10-02)
//   - Staleness tick for online player derivation
//   - GM auto-cleanup of stale player docs (60s) - the SEAT, never the membership
// ============================================================

import { useState, useEffect } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { toMs } from '../utils/timestamps';
import type { SessionPlayer } from '../types';
import type { CollectionRead } from '../backend/types';
import type * as mpModule from '../multiplayer';
import { diag, errorSummary } from '../diagnostics/log';
import { withRetry, type RetryDelay } from '../utils/retry';
import { isLineFailure } from '../utils/lineFailure';
import useStableList from './useStableList';
import { canonicalJson } from '../engine/canonicalJson';
import { keepUnchanged } from '../utils/keepUnchanged';

const PLAYER_STALE_MS = 45000;
const PLAYER_CLEANUP_MS = 300000;
const HEARTBEAT_INTERVAL_MS = 15000;
const STALENESS_TICK_MS = 15000;
const GM_CLEANUP_INTERVAL_MS = 60000;
/** A re-seat through a dropped line: 1 s, doubling, capped - as the listeners back off. */
const RESEAT_BASE_MS = 1000;
const RESEAT_CAP_MS = 30000;

interface SessionState {
  id: string | null;
  code: string | null;
  isGm: boolean;
  playerId: string | null;
}

interface UsePlayerPresenceParams {
  sessionId: string | null;
  isGm: boolean;
  playerId: string | null;
  user: { sessionName?: string; displayName?: string | null };
  setSession: Dispatch<SetStateAction<SessionState>>;
  setConnectedPlayers: Dispatch<SetStateAction<SessionPlayer[]>>;
  /** Multiplayer service (DI) */
  mp: typeof mpModule;
}

type Mp = typeof mpModule;
type PlayersRead = CollectionRead<Record<string, unknown>>;

const displayNameOf = (user: UsePlayerPresenceParams['user']) => user.sessionName || user.displayName || 'Unknown';

/** Online: not marked offline, and heard from within the stale window. */
function isOnline(p: SessionPlayer, now: number): boolean {
  if (p.online === false) return false;
  const ms = toMs(p.lastSeen);
  return ms > 0 && now - ms < PLAYER_STALE_MS;
}

/**
 * GM first, then by name (then seat, for two of one name). A snapshot's own
 * order is not stable - it reshuffled mid-kick in e2e/presence - and a list
 * that moves under the pointer kicks the player beside the one aimed at.
 */
function byTableOrder(a: SessionPlayer, b: SessionPlayer): number {
  return (
    Number(!!b.isGm) - Number(!!a.isGm) ||
    (a.displayName ?? '').localeCompare(b.displayName ?? '') ||
    a.id.localeCompare(b.id)
  );
}

/** Non-GM seats silent for longer than the cleanup window. */
function staleSeats(players: SessionPlayer[], now: number): SessionPlayer[] {
  return players.filter((p) => {
    if (p.isGm) return false;
    const ms = toMs(p.lastSeen);
    return ms > 0 && now - ms > PLAYER_CLEANUP_MS;
  });
}

/** One seat watch's own state, born and let go with it. */
interface SeatState {
  /** Has this seat ever been seen in a read? Only then can its absence mean anything. */
  confirmed: boolean;
  /** A re-seat is under way: one at a time, or every view while it waits takes another seat. */
  reseating: boolean;
  /** The watch was let go (session left, seat changed, page closed): stop retrying. */
  closed: boolean;
}

interface SeatWatch {
  sessionId: string;
  playerId: string;
  name: string;
  mp: Mp;
  setSession: UsePlayerPresenceParams['setSession'];
  seat: SeatState;
  onPlayers: (players: SessionPlayer[]) => void;
}

const LEFT = { id: null, code: null, isGm: false, playerId: null };

/**
 * When to try the rejoin again: only while the LINE is what failed, and
 * someone still wants the seat. Uncapped in count - the line comes back
 * when it comes back - and capped in step.
 */
function reseatDelay({ mp, seat }: SeatWatch): RetryDelay {
  return (err, attempt) => {
    if (seat.closed || !isLineFailure(err, mp.errorKind(err))) return null;
    const delay = Math.min(RESEAT_CAP_MS, RESEAT_BASE_MS * 2 ** attempt);
    diag.warn('presence', `re-seat failed: the line is down - retry ${attempt + 1} in ${delay}ms`, errorSummary(err));
    return delay;
  };
}

/**
 * The seat went missing - from a complete view, or under a heartbeat's
 * update: take a new one. Once per seat watch, whichever noticed first.
 *
 * A MISSING SEAT IS NOT A KICK (v8.310.4). The GM's prune removes a seat
 * whose heartbeats stopped reaching the server - which, with the client
 * alive enough to receive this very snapshot, means the writes were
 * failing, not the person gone. Membership is intact, so take a new seat;
 * only a rejoin the server refuses means it really is over.
 *
 * AND A REJOIN THAT NEVER REACHED THE SERVER IS NOT A REFUSAL (2026-10-02).
 * Those failing writes are a failing line, so the rejoin usually fails the
 * same way - status 0 on PocketBase, `unavailable` on Firestore - and was
 * read as "refused": every dropped player was thrown out of the session.
 * It now waits for the line and tries again; only an answer ends it.
 */
function reseat(watch: SeatWatch, why: string) {
  const { sessionId, name, mp, setSession, seat } = watch;
  if (seat.closed || seat.reseating) return;
  seat.reseating = true;
  diag.warn('presence', `${why} - re-seating`);
  // The guard: a retry whose wait outlived the watch never goes out.
  const rejoin = () =>
    seat.closed ? Promise.reject(new Error('seat watch let go')) : mp.rejoinSession(sessionId, name);
  withRetry(rejoin, reseatDelay(watch), Infinity)
    .then((r: { playerId: string }) => {
      if (seat.closed) return;
      diag.info('presence', `re-seated as ${r.playerId}`);
      setSession((s) => ({ ...s, playerId: r.playerId }));
    })
    .catch((e: unknown) => {
      if (seat.closed) return;
      diag.error('presence', 're-seat refused - leaving the session', errorSummary(e));
      setSession(LEFT);
    });
}

/**
 * One players read: publish the list, and notice whether my seat is in it.
 *
 * ONLY A COMPLETE VIEW MAY THROW YOU OUT. A snapshot taken while writes
 * are in flight lists only some of the players - and if the one it happens
 * to omit is YOU, this ejected you from the session you are sitting in.
 * `listsEveryone` is the difference between "you have been removed" and
 * "the server has not mentioned you yet" (v8.25.0).
 */
function onPlayersRead({ entries, listsEveryone }: PlayersRead, watch: SeatWatch) {
  const players = entries.map((e) => ({ id: e.id, ...e.data }) as SessionPlayer);
  watch.onPlayers(players);
  const { seat } = watch;
  if (players.some((p) => p.id === watch.playerId)) seat.confirmed = true;
  else if (seat.confirmed && listsEveryone) reseat(watch, 'own seat missing from a complete view');
}

/** Subscribe to the players list; a listener that fails to start is logged, not thrown. */
function watchSeat(watch: SeatWatch): (() => void) | undefined {
  try {
    return watch.mp.onPlayersUpdate(watch.sessionId, (read) => onPlayersRead(read, watch));
  } catch (e) {
    console.error('[PLAYERS] Listener error:', e);
    return undefined;
  }
}

/**
 * A seat write's answer. `false` is the seat GONE - the GM's prune took it
 * while the line was down - and a heartbeat may not put it back (that was
 * a seat with no name, 2026-10-03), so take a new one, as the listener
 * does when it sees the same thing first.
 */
const seatAnswered = (watch: SeatWatch) => (landed: boolean) => {
  if (landed === false) reseat(watch, 'own seat gone - a heartbeat found nothing to update');
};

/**
 * Announce the seat, beat every 15 s, and mark it offline on the way out
 * (page hide, unload, or the returned cleanup). A FAILED HEARTBEAT IS
 * NEWS: swallowed, five minutes of them read as a player who left - and
 * the GM's prune acted on it (v8.310.0).
 */
function keepSeatWarm(watch: SeatWatch): () => void {
  const { mp, sessionId, playerId, name } = watch;
  const beat = (fields: Record<string, unknown>, failure: string) =>
    mp
      .setPlayerProfile(sessionId, playerId, { online: true, lastSeen: new Date(), ...fields })
      .then(seatAnswered(watch))
      .catch((e: unknown) => diag.warn('presence', failure, errorSummary(e)));
  const hb = setInterval(() => beat({}, 'heartbeat failed'), HEARTBEAT_INTERVAL_MS);
  beat({ displayName: name }, 'could not announce presence');
  const markOffline = () => {
    mp.setPlayerProfile(sessionId, playerId, { online: false }).catch((e: unknown) =>
      diag.warn('presence', 'could not mark offline', errorSummary(e)),
    );
  };
  window.addEventListener('beforeunload', markOffline);
  window.addEventListener('pagehide', markOffline);
  return () => {
    clearInterval(hb);
    window.removeEventListener('beforeunload', markOffline);
    window.removeEventListener('pagehide', markOffline);
    markOffline();
  };
}

/**
 * A player as the table shows them: every field but the one that ticks on its own - the presence clock, with each
 * heartbeat. (The diagnostics left the seat for `seatDiag`, 2026-10-31.) CANONICAL, not `JSON.stringify`: Firestore
 * moves a field it just wrote to the end of the object, and the same seat then read as a different one every beat.
 */
const shownOf = (p: SessionPlayer): string => canonicalJson({ ...p, lastSeen: null });

export default function usePlayerPresence(params: UsePlayerPresenceParams): { onlinePlayers: SessionPlayer[] } {
  const { sessionId, isGm, mp } = params;
  const [players, setPlayers] = useState<SessionPlayer[]>([]);
  useSeat(params, setPlayers);
  const stalenessTick = useStalenessTick(sessionId);
  void stalenessTick; // referenced so React re-evaluates onlinePlayers on tick
  const now = Date.now();
  // The same array across a heartbeat (plan shell-contexts, A3): the session context holds it, and every panel that
  // reads the context rendered with each player's beat. Everything but the presence clock counts as a change.
  const onlinePlayers = useStableList(players.filter((p) => isOnline(p, now)).sort(byTableOrder), shownOf);
  useGmPrune(sessionId, isGm, players, mp);
  return { onlinePlayers };
}

/**
 * Where a delivery's seats go: this hook's list and the shell's. A delivery that repeats the seats sets nothing (plan
 * sync-listeners): every seat write re-delivered every seat as new objects, and the shell rendered for each. A
 * heartbeat still moves `lastSeen`, and that is a change.
 */
function publishSeats(...setters: Dispatch<SetStateAction<SessionPlayer[]>>[]): (players: SessionPlayer[]) => void {
  let held: SessionPlayer[] = [];
  return (players) => {
    const next = keepUnchanged(held, players, (p) => p.id);
    if (next === held) return;
    held = next;
    for (const set of setters) set(next);
  };
}

/** The player listener and this seat's heartbeat, for as long as there is a seat. */
function useSeat(params: UsePlayerPresenceParams, setPlayers: Dispatch<SetStateAction<SessionPlayer[]>>) {
  const { sessionId, playerId, user, setSession, setConnectedPlayers, mp } = params;
  useEffect(() => {
    if (!sessionId || !playerId) return;
    const seat: SeatState = { confirmed: false, reseating: false, closed: false };
    const onPlayers = publishSeats(setPlayers, setConnectedPlayers);
    const watch = { sessionId, playerId, name: displayNameOf(user), mp, setSession, seat, onPlayers };
    const unsub = watchSeat(watch);
    const cool = keepSeatWarm(watch);
    return () => {
      seat.closed = true;
      if (unsub) unsub();
      cool();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, playerId]);
}

/** A counter bumped every 15 s, so the online list is re-evaluated as time passes. */
function useStalenessTick(sessionId: string | null): number {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!sessionId) return;
    const iv = setInterval(() => setTick((t) => t + 1), STALENESS_TICK_MS);
    return () => clearInterval(iv);
  }, [sessionId]);
  return tick;
}

/**
 * GM auto-cleanup of stale player docs - the SEAT only. Revoking
 * membership here is what turned a throttled proxy into a table-wide kick
 * (v8.310.4); the kick button keeps `deletePlayer` for itself.
 */
function useGmPrune(sessionId: string | null, isGm: boolean, players: SessionPlayer[], mp: Mp) {
  useEffect(() => {
    if (!sessionId || !isGm) return;
    const iv = setInterval(() => {
      for (const p of staleSeats(players, Date.now())) {
        diag.info('presence', `pruning stale seat ${p.id}`);
        mp.expirePresence(sessionId, p.id).catch((e: unknown) =>
          diag.error('presence', 'prune failed', errorSummary(e)),
        );
      }
    }, GM_CLEANUP_INTERVAL_MS);
    return () => clearInterval(iv);
  }, [sessionId, isGm, players, mp]);
}
